export interface ParsedStockSheet {
  name: string;
  rows: unknown[][];
}

export interface ParsedStockReport {
  fileName: string;
  sheets: ParsedStockSheet[];
}

const cellToValue = (value: any): unknown => {
  if (value === null || value === undefined) return "";
  if (value instanceof Date) return value.toISOString();
  if (typeof value === "object") {
    if (Array.isArray(value.richText)) {
      return value.richText.map((part: any) => part?.text || "").join("");
    }
    if (value.text !== undefined) return String(value.text);
    if (value.result !== undefined) return cellToValue(value.result);
  }
  return value;
};

const parseCsvLine = (line: string, separator: string) => {
  const values: string[] = [];
  let current = "";
  let inQuotes = false;
  for (let index = 0; index < line.length; index += 1) {
    const char = line[index];
    const next = line[index + 1];
    if (char === '"') {
      if (inQuotes && next === '"') {
        current += '"';
        index += 1;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (char === separator && !inQuotes) {
      values.push(current.trim());
      current = "";
    } else {
      current += char;
    }
  }
  values.push(current.trim());
  return values;
};

const parseCsv = async (file: File): Promise<ParsedStockReport> => {
  const text = await file.text();
  const lines = text.replace(/\r\n/g, "\n").replace(/\r/g, "\n").split("\n");
  const firstLine = lines.find((line) => line.trim()) || "";
  const separator = firstLine.includes(";") ? ";" : ",";
  const rows = lines
    .filter((line) => line.trim())
    .map((line) => parseCsvLine(line, separator));
  return { fileName: file.name, sheets: [{ name: "CSV", rows }] };
};

export const parseStockReportFile = async (file: File): Promise<ParsedStockReport> => {
  if (/\.csv$/i.test(file.name)) return parseCsv(file);

  const ExcelJS = await import("exceljs");
  const workbook = new ExcelJS.Workbook();
  await workbook.xlsx.load(await file.arrayBuffer());
  const sheets = workbook.worksheets.map((worksheet) => ({
    name: worksheet.name,
    rows: Array.from({ length: worksheet.rowCount }, (_, rowIndex) => {
      const values = worksheet.getRow(rowIndex + 1).values;
      return Array.isArray(values) ? values.slice(1).map(cellToValue) : [];
    }),
  }));
  return { fileName: file.name, sheets };
};

export const getStockReportHeaders = (
  report: ParsedStockReport,
  sheetName: string,
  headerRow: number
) => {
  const sheet = report.sheets.find((item) => item.name === sheetName);
  return (sheet?.rows[Math.max(0, headerRow - 1)] || [])
    .map((value) => String(value ?? "").trim())
    .filter(Boolean);
};

export const getStockReportDataRows = (
  report: ParsedStockReport,
  sheetName: string,
  headerRow: number
) => {
  const sheet = report.sheets.find((item) => item.name === sheetName);
  if (!sheet) return { headers: [] as string[], rows: [] as unknown[][] };
  const rawHeaders = sheet.rows[Math.max(0, headerRow - 1)] || [];
  const headers = rawHeaders.map((value) => String(value ?? "").trim());
  const rows = sheet.rows
    .slice(headerRow)
    .filter((row) => row.some((value) => String(value ?? "").trim() !== ""));
  return { headers, rows };
};

export const inferStockImportMapping = (headers: string[]) => {
  const normalized = new Map(
    headers.map((header) => [
      header
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase(),
      header,
    ])
  );
  const find = (...patterns: string[]) => {
    for (const [key, original] of normalized.entries()) {
      if (patterns.some((pattern) => key.includes(pattern))) return original;
    }
    return undefined;
  };
  return {
    productCode: find("code article", "sku", "reference"),
    barcode: find("gencod", "barcode", "ean"),
    quantity: find("qte en stock uvc", "quantite", "quantity", "stock uvc"),
    batchNumber: find("n° lot", "n lot", "lot"),
    expiryDate: find("dluo", "dlu", "expiration"),
  };
};
